/**
 * Slider/Carousel - Modern Implementation
 *
 * NOTE: This file contains a fallback for Slick carousel (jQuery dependency).
 * For best performance, consider migrating to Swiper.js (zero dependencies).
 *
 * Migration guide:
 * 1. Install: npm install swiper
 * 2. Import: import Swiper from 'swiper/bundle';
 * 3. Replace initialization below
 *
 * @package ultamation
 * @since 3.1.0
 */

import { ready, $$, $ } from "./utils.js";

ready(() => {
  // Check if we have carousel elements
  const carousels = $$(
    ".owl-carousel, .content-split__images--carousel, .hero-block__images--carousel, .cards-block__cards--carousel",
  );

  if (carousels.length === 0) return;

  // Check if jQuery and Slick are available (legacy support)
  if (typeof jQuery !== "undefined" && typeof jQuery.fn.slick !== "undefined") {
    initSlickCarousel();
  } else {
    // Fallback: Basic native carousel or warning
    console.warn(
      "Slick carousel not available. Consider migrating to Swiper.js for better performance.",
    );
    initNativeCarousel(carousels);
  }
});

/**
 * Initialize Slick carousel (legacy jQuery-based)
 */
function initSlickCarousel() {
  const $ = window.jQuery;
  const $carousel = $(
    ".owl-carousel, .content-split__images--carousel, .hero-block__images--carousel, .cards-block__cards--carousel",
  );

  if ($carousel.length === 0) return;

  // Prevent CLS by setting explicit height before initialization
  $carousel.each(function () {
    const $this = $(this);

    // Only process if it hasn't been initialized yet
    if ($this.hasClass("slick-initialized")) return;

    const firstImage = $this.find("img").first();

    if (firstImage.length) {
      // Set min-height based on image dimensions or natural height
      if (firstImage[0].complete && firstImage[0].naturalHeight > 0) {
        const height = firstImage.height();
        if (height > 0) {
          $this.css("min-height", height + "px");
        }
      } else {
        // Image not loaded yet, wait for it
        firstImage.on("load", function () {
          const height = $(this).height();
          if (height > 0) {
            $this.css("min-height", height + "px");
          }
        });
      }
    }
  });

  // Small delay to ensure images are processed
  setTimeout(() => {
    $carousel.not(".slick-initialized").each(function () {
      const $this = $(this);
      const isCardsCarousel = $this.hasClass("cards-block__cards--carousel");

      // Different config for cards carousel vs image carousels
      const config = isCardsCarousel
        ? {
          slidesToShow: 4,
          slidesToScroll: 1,
          vertical: false,
          arrows: true,
          dots: false,
          autoplay: false,
          adaptiveHeight: false,
          fade: false,
          cssEase: "ease-out",
          speed: 300,
          waitForAnimate: true,
          responsive: [
            {
              breakpoint: 1024,
              settings: {
                slidesToShow: 3,
              },
            },
            {
              breakpoint: 768,
              settings: {
                slidesToShow: 2,
              },
            },
            {
              breakpoint: 480,
              settings: {
                slidesToShow: 1,
              },
            },
          ],
          prevArrow:
            '<button class="slick-prev" aria-label="Previous slide"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="12" viewBox="0 0 20 12" fill="none" aria-hidden="true"><path d="M20 10.1117L18.225 11.8867L10 3.66172L1.775 11.8867L0 10.1117L10 0.111718L20 10.1117Z" fill="#151820"/></svg></button>',
          nextArrow:
            '<button class="slick-next" aria-label="Next slide"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="12" viewBox="0 0 20 12" fill="none" aria-hidden="true"><path d="M20 1.88828L18.225 0.113281L10 8.33828L1.775 0.113281L0 1.88828L10 11.8883L20 1.88828Z" fill="#151820"/></svg></button>',
        }
        : {
          slidesToShow: 1,
          vertical: false,
          arrows: true,
          dots: false,
          autoplay: true,
          adaptiveHeight: false,
          fade: false,
          cssEase: "ease-out",
          speed: 300,
          waitForAnimate: true,
          prevArrow:
            '<button class="slick-prev" aria-label="Previous slide"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="12" viewBox="0 0 20 12" fill="none" aria-hidden="true"><path d="M20 10.1117L18.225 11.8867L10 3.66172L1.775 11.8867L0 10.1117L10 0.111718L20 10.1117Z" fill="#151820"/></svg></button>',
          nextArrow:
            '<button class="slick-next" aria-label="Next slide"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="12" viewBox="0 0 20 12" fill="none" aria-hidden="true"><path d="M20 1.88828L18.225 0.113281L10 8.33828L1.775 0.113281L0 1.88828L10 11.8883L20 1.88828Z" fill="#151820"/></svg></button>',
        };

      $this.slick(config);
    });
  }, 50);
}

/**
 * Initialize native CSS-based carousel (no dependencies)
 */
function initNativeCarousel(carousels) {
  carousels.forEach((carousel) => {
    const slides = carousel.querySelectorAll("img, .slide");
    if (slides.length <= 1) return; // No need for carousel with 1 slide

    let currentSlide = 0;

    // Wrap slides in proper structure
    carousel.style.position = "relative";
    carousel.style.overflow = "hidden";

    slides.forEach((slide, index) => {
      slide.style.position = "absolute";
      slide.style.top = "0";
      slide.style.left = "0";
      slide.style.width = "100%";
      slide.style.opacity = index === 0 ? "1" : "0";
      slide.style.transition = "opacity 0.3s ease";
    });

    // Create navigation buttons
    const prevBtn = document.createElement("button");
    prevBtn.className = "carousel-prev";
    prevBtn.setAttribute("aria-label", "Previous slide");
    prevBtn.innerHTML =
      '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="12" viewBox="0 0 20 12" fill="none"><path d="M20 10.1117L18.225 11.8867L10 3.66172L1.775 11.8867L0 10.1117L10 0.111718L20 10.1117Z" fill="#151820"/></svg>';

    const nextBtn = document.createElement("button");
    nextBtn.className = "carousel-next";
    nextBtn.setAttribute("aria-label", "Next slide");
    nextBtn.innerHTML =
      '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="12" viewBox="0 0 20 12" fill="none"><path d="M20 1.88828L18.225 0.113281L10 8.33828L1.775 0.113281L0 1.88828L10 11.8883L20 1.88828Z" fill="#151820"/></svg>';

    carousel.appendChild(prevBtn);
    carousel.appendChild(nextBtn);

    // Navigation logic
    function showSlide(index) {
      slides[currentSlide].style.opacity = "0";
      currentSlide = (index + slides.length) % slides.length;
      slides[currentSlide].style.opacity = "1";
    }

    prevBtn.addEventListener("click", (e) => {
      e.preventDefault();
      showSlide(currentSlide - 1);
    });

    nextBtn.addEventListener("click", (e) => {
      e.preventDefault();
      showSlide(currentSlide + 1);
    });

    // Auto-play
    setInterval(() => {
      showSlide(currentSlide + 1);
    }, 3000);
  });
}

jQuery(document).ready(function ($) {

  $('.hero-banner').each(function () {

    var $section = $(this);
    var $slider = $section.find('.hero-slider');
    var $dots = $section.find('.hero-dots');

    // Prevent duplicate initialization
    if ($slider.hasClass('slick-initialized')) {
      return;
    }

    $slider.slick({
      slidesToShow: 1,
      slidesToScroll: 1,
      arrows: false,
      dots: true,
      appendDots: $dots,
      autoplay: true,
      autoplaySpeed: 4000,
      speed: 800,
      infinite: true,
      fade: true,
      cssEase: 'ease-in-out',
    });

  });

  $('.testimonial-slider').slick({
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    arrows: false,
    autoplay: true,
    autoplaySpeed: 3000,
    adaptiveHeight: false
  });

  $('.image-description-slider').each(function () {

    var $wrapper = $(this);
    var $slider = $wrapper.find('.image-slider');
    var $counter = $wrapper.find('.slider-counter');

    function formatNumber(num) {
      return num < 10 ? '0' + num : num;
    }

    // INIT
    $slider.on('init', function (event, slick) {
      $counter.find('.total').text(formatNumber(slick.slideCount));
      $counter.find('.current').text(formatNumber(1));
    });

    // AFTER CHANGE
    $slider.on('afterChange', function (event, slick, currentSlide) {
      $counter.find('.current').text(formatNumber(currentSlide + 1));
    });

    // SLICK INIT
    $slider.slick({
      dots: false,
      infinite: true,
      speed: 500,
      slidesToShow: 1,
      arrows: false,
      autoplay: true,
      autoplaySpeed: 3000,
      adaptiveHeight: true
    });

  });

});

jQuery(document).ready(function ($) {

  function initProjectSlider() {
    var $slider = $('.bespoke-projects .project-grid');

    if ($(window).width() < 768) {
      if (!$slider.hasClass('slick-initialized')) {
        $slider.slick({
          slidesToShow: 1,
          slidesToScroll: 1,
          arrows: false,
          dots: true,
          infinite: false,
          adaptiveHeight: true
        });
      }
    } else {
      if ($slider.hasClass('slick-initialized')) {
        $slider.slick('unslick');
      }
    }
  }

  // Init on load
  initProjectSlider();

  // Re-init on resize
  $(window).on('resize', function () {
    initProjectSlider();
  });

});

jQuery(document).ready(function ($) {

  function initProjectSlider() {
    var $slider = $('.related-projects .projects-wrapper');

    if ($(window).width() < 768) {
      if (!$slider.hasClass('slick-initialized')) {
        $slider.slick({
          slidesToShow: 1,
          slidesToScroll: 1,
          arrows: false,
          dots: true,
          infinite: false,
          adaptiveHeight: true
        });
      }
    } else {
      if ($slider.hasClass('slick-initialized')) {
        $slider.slick('unslick');
      }
    }
  }

  // Init on load
  initProjectSlider();

  // Re-init on resize
  $(window).on('resize', function () {
    initProjectSlider();
  });

});

jQuery(document).ready(function ($) {

  function initLatestNewsSlider() {
    $('.latest-news .news-grid').each(function () {
      var $slider = $(this);

      if ($(window).width() < 768) {
        if (!$slider.hasClass('slick-initialized')) {
          $slider.slick({
            slidesToShow: 1,
            slidesToScroll: 1,
            arrows: false,
            dots: true,
            infinite: false,
            adaptiveHeight: true
          });
        }
      } else if ($slider.hasClass('slick-initialized')) {
        $slider.slick('unslick');
      }
    });
  }

  initLatestNewsSlider();

  $(window).on('resize', function () {
    initLatestNewsSlider();
  });

});

jQuery(document).ready(function ($) {

  function initProjectSlider() {
    var $slider = $('.team-section .team-grid');

    if ($(window).width() < 1024) {
      if (!$slider.hasClass('slick-initialized')) {
        $slider.slick({
          slidesToShow: 3,
          slidesToScroll: 1,
          arrows: false,
          dots: true,
          infinite: false,
          adaptiveHeight: true,
          responsive: [
            {
              breakpoint: 767,
              settings: {
                slidesToShow: 2
              }
            },
            {
              breakpoint: 575,
              settings: {
                slidesToShow: 1
              }
            }
          ]
        });
      }
    } else {
      if ($slider.hasClass('slick-initialized')) {
        $slider.slick('unslick');
      }
    }
  }

  // Init on load
  initProjectSlider();

  // Re-init on resize
  $(window).on('resize', function () {
    initProjectSlider();
  });

});

jQuery(document).ready(function ($) {

  function initRelatedProductsSlider() {
    var $targets = $('.related-products-slider, .related-products .product-grid');

    $targets.each(function () {
      var $el = $(this);
      var $slider = $el.hasClass('product-grid') ? $el : ($el.find('.product-grid').length ? $el.find('.product-grid') : $el);

      if ($slider.hasClass('slick-initialized')) {
        return;
      }

      var slideCount = $slider.children('.product-card').length || $slider.children().length;

      if (slideCount > 1) {
        $slider.slick({
          slidesToShow: 3,
          slidesToScroll: 1,
          arrows: true,
          dots: false,
          infinite: true,
          autoplay: true,
          autoplaySpeed: 3000,
          adaptiveHeight: false,
          responsive: [
            {
              breakpoint: 1024,
              settings: {
                slidesToShow: 2,
                slidesToScroll: 1,
                arrows: true,
                dots: false
              }
            },
            {
              breakpoint: 768,
              settings: {
                slidesToShow: 1,
                slidesToScroll: 1,
                arrows: true,
                dots: false
              }
            }
          ]
        });
      }
    });
  }

  // Init on load
  initRelatedProductsSlider();

  // Re-init on resize (debounced for performance)
  var resizeTimer;
  $(window).on('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      initRelatedProductsSlider();
    }, 200);
  });

});

jQuery(document).ready(function ($) {

  function formatNumber(num) {
    return num < 10 ? '0' + num : num;
  }

  $('.bespoke-banner').each(function () {

    var $wrapper = $(this);
    var $slider = $wrapper.find('.bespoke-right-slider');

    // Initialize image slider
    $slider.slick({
      slidesToShow: 1,
      slidesToScroll: 1,
      arrows: false,
      dots: false,
      infinite: true,
      speed: 1000,
      autoplay: true,
      autoplaySpeed: 3000,
      pauseOnHover: true,
      pauseOnFocus: false,
      draggable: true,
      swipe: true
    });

  });
  $('.inner-banner').each(function () {

    var $wrapper = $(this);
    var $slider = $wrapper.find('.inner-banner-slider');

    // Initialize image slider
    $slider.slick({
      slidesToShow: 1,
      slidesToScroll: 1,
      arrows: false,
      dots: false,
      infinite: true,
      speed: 1000,
      autoplay: true,
      autoplaySpeed: 3000,
      pauseOnHover: true,
      pauseOnFocus: false,
      draggable: true,
      swipe: true
    });

  });
});

jQuery(document).ready(function ($) {

  function formatNumber(num) {
    return num < 10 ? '0' + num : num;
    // OR: return String(num).padStart(2, '0');
  }

  $('.bespoke-inner-banner').each(function () {

    var $wrapper = $(this);
    var $slider = $wrapper.find('.bespoke-slider');
    var $counter = $wrapper.find('.slider-counter');

    // INIT (before slick)
    $slider.on('init', function (event, slick) {
      $counter.find('.total').text(formatNumber(slick.slideCount));
      $counter.find('.current').text(formatNumber(1));
    });

    // AFTER CHANGE
    $slider.on('afterChange', function (event, slick, currentSlide) {
      $counter.find('.current').text(formatNumber(currentSlide + 1));
    });

    // INIT SLICK
    $slider.slick({
      dots: false,
      infinite: true,
      speed: 500,
      slidesToShow: 1,
      arrows: false,
      autoplay: true,
      autoplaySpeed: 3000,
      adaptiveHeight: true
    });

  });

});

jQuery(document).ready(function ($) {
  jQuery('.articles-slider .articles-item').length > 3 && jQuery('.articles-slider').slick({
    slidesToShow: 3,
    slidesToScroll: 1,
    arrows: true,
    dots: false,
    infinite: true,
    speed: 600,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2
        }
      },
      {
        breakpoint: 768,
        settings: {
          dots: true,
          slidesToShow: 1
        }
      }
    ]
  });
});

jQuery(document).ready(function ($) {

  $('.product-video-slider').slick({
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
    dots: true,
    adaptiveHeight: true
  });

});

jQuery(function ($) {

  function initProductGallery() {

    var $wrapper = $('.woocommerce-product-gallery__wrapper');
    var $item = $('.woocommerce-product-gallery__image');

    if ($item.length <= 1) {
      return;
    }

    if ($wrapper.hasClass('slick-initialized')) {
      $wrapper.slick('unslick');
    }

    $wrapper.slick({
      slidesToShow: 1,
      slidesToScroll: 1,
      arrows: true,
      rows: false,
      dots: true,
      infinite: true,
      autoplay: true,
      autoplaySpeed: 3000,
      speed: 600,
      draggable: true,
      swipe: true,
      touchMove: true,
      adaptiveHeight: false
    });

  }

  $(window).on('load', initProductGallery);

});